"use client"

import { createContext, useCallback, useContext, type ReactNode } from "react"

import { type Locale, content } from "./content"
import { localePath } from "./seo-paths"

type LocaleContextType = {
  locale: Locale
  t: (typeof content)[Locale]
  /** The public path of a page in the current language. */
  href: (path: string) => string
}

const LocaleContext = createContext<LocaleContextType | undefined>(undefined)

/** The language comes from the URL (/, /pt, /de), so every language is its own indexable page. */
export function LocaleProvider({ locale, children }: { locale: Locale; children: ReactNode }) {
  const href = useCallback((path: string) => localePath(locale, path), [locale])
  return (
    <LocaleContext.Provider value={{ locale, t: content[locale], href }}>
      {children}
    </LocaleContext.Provider>
  )
}

export function useLocale() {
  const context = useContext(LocaleContext)
  if (!context) {
    throw new Error("useLocale must be used within a LocaleProvider")
  }
  return context
}
